import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { MultiPolygon, Polygon } from 'geojson';
import { useAtom, useAtomValue } from 'jotai';
import type { ValueOf } from 'type-fest';

import { selectedVisitAtom } from '../app/appHeader/imageViewerMenu/atoms';

import { makeAPIRequestReactQuery } from './api';
import type { TFmxSummary } from './useFmxSummaries';
import { useFmxSummaries } from './useFmxSummaries';
import { usePreferences } from './usePreferences';

export const useSegmentations = (
  args?: {
    estimatedVisitId?: number;
  },
  options?: UseQueryOptions<TSegmentations, unknown, TSegmentations>,
) => {
  const [selectedEstimatedVisitId] = useAtom(selectedVisitAtom);
  const estimatedVisitId = args?.estimatedVisitId || selectedEstimatedVisitId;
  const fmxSummaryData = useFmxSummaries(
    [
      [
        {
          estimatedVisitId:
            typeof estimatedVisitId === 'string'
              ? Number(estimatedVisitId)
              : estimatedVisitId,
        },
      ],
    ],
    true,
  );
  const { data: prefs } = usePreferences();
  let fmxSummary: TFmxSummary | undefined = undefined;
  if (fmxSummaryData?.[0]?.data?.[0]?.summary) {
    fmxSummary = fmxSummaryData[0].data?.[0];
  }

  return useQuery<TSegmentations>(
    [estimatedVisitId, 'analyses', 'segmentations'],
    () =>
      makeAPIRequestReactQuery<TSegmentations>(
        `${estimatedVisitId}/analyses/segmentations`,
      ),
    {
      enabled: !!estimatedVisitId,
      select(data) {
        if (!prefs?.rolloutAverageDmfImageViewer || !data) {
          return data;
        }
        return data.map((segmentation) => ({
          ...segmentation,
          teeth: segmentation.teeth?.map((tooth) => ({
            ...tooth,
            dmf: fmxSummary?.summary?.[tooth.toothId]?.dmf,
          })),
        }));
      },
      ...options,
    },
  );
};

export const useGetImageWithSegmentations = (
  activeImageId?: string,
  visitId?: string,
) => {
  const selectedVisitIdAtomValue = useAtomValue(selectedVisitAtom);
  const selectedVisitId = visitId || selectedVisitIdAtomValue;
  const { data: preferences } = usePreferences();
  const { data } = useSegmentations({
    estimatedVisitId: selectedVisitId ? parseInt(selectedVisitId) : undefined,
  });
  const imageWithSegmentations =
    data &&
    data?.find(
      (image) =>
        // If we have an active image id it should match one of the segmentations results
        (!activeImageId || image.imageId === activeImageId) &&
        image.teeth?.find(
          (tooth) =>
            (tooth.caries?.length &&
              tooth.caries.find((carie) => !!carie.outline?.coordinates)) ||
            (preferences?.displayPRLSegmentations &&
              tooth.periapicalRadiolucency?.length &&
              tooth.periapicalRadiolucency.find(
                (prl) => !!prl.outline?.coordinates,
              )) ||
            (preferences?.displayRALSegmentations &&
              tooth.restorativeAdjacentLucency?.length &&
              tooth.restorativeAdjacentLucency.find(
                (ral) => !!ral.outline?.coordinates,
              )) ||
            (tooth.enamel && tooth.enamel.coordinates.length > 0) ||
            (tooth.pulp && tooth.pulp.coordinates.length > 0),
        ),
    );
  return imageWithSegmentations;
};

export type TSegmentations = {
  /**
   * This is either the imageanalyses._id from mongo
   * or the "NonPhi"."ImageManifests".id from postgres
   */
  imageId: string;
  teeth?: {
    decayOnCrown?: number;
    /**
     * This is the tooth number/identifier, not the PK of the teeth record.
     */
    toothId: string;
    /**
     * dmf
     */
    dmf?: number;
    /**
     * isPartial
     */
    isPartial?: number;
    /**
     * TDIS
     */
    outline?: Polygon;
    /**
     * segmentation - tooth structure
     */
    enamel?: MultiPolygon;
    /**
     * segmentation - tooth structure
     */
    crownDentin?: MultiPolygon;
    /**
     * segmentation - tooth structure
     */
    rootDentin?: MultiPolygon;
    /**
     * segmentation - tooth structure
     */
    pulp?: MultiPolygon;
    /**
     * segmentation - tooth structure
     */
    interproximalConcavities?: MultiPolygon;
    /**
     * segmentation - caries
     */
    caries?: Array<
      TSegmentation & {
        severity: TCariesSeverity;
        /**
         * segmentation - lower confidence score band
         */
        confidenceOutlineLow?: MultiPolygon;
        /**
         * segmentation - higher confidence score band
         */
        confidenceOutlineHigh?: MultiPolygon;
        /**
         * is this an area of concern or not?
         */
        isAoc: boolean;
        /**
         * % into enamel
         */
        //intoEnamel?: number;
        /**
         * % into crownDentin
         */
        //intoCrownDentin?: number;
        /**
         * % into rootDentin
         */
        /**
         * @deprecated this is calculated in postgis without clinical input.
         * please use depth or rootInvolvement
         */
        intoRootDentin?: number;
        /**
         * @deprecated this is calculated in postgis without clinical input.
         * please use depth
         */
        intoPulp?: number;
        /**
         * fmx summary - python
         */
        depth?: TCariesDepth;
        /**
         * fmx summary - python
         */
        rootInvolvement?: TCariesRootInvolvement;
        /**
         * nucleus for VCA or straight from VDA
         * this is the bounding box: xMin_yMin_xMax_yMax
         * if vda then _caries
         * else _vca_caries
         */
        findingIdentifier?: `${number}_${number}_${number}_${number}_${
          | 'caries'
          | 'vca_caries'}`;
      }
    >;
    calculus?: TSegmentation[];
    periapicalRadiolucency?: TSegmentation[];
    restorativeAdjacentLucency?: TSegmentation[];
  }[];
}[];

export const CariesDepth = {
  into_crown_and_root_dentin: 'into_crown_and_root_dentin',
  into_crown_dentin: 'into_crown_dentin',
  into_enamel: 'into_enamel',
  into_furcation_and_root_dentin: 'into_furcation_and_root_dentin',
  into_furcation_and_root_dentin_and_pulp:
    'into_furcation_and_root_dentin_and_pulp',
  into_root_dentin: 'into_root_dentin',
  into_pulp: 'into_pulp',
  into_pulp_and_crown_dentin: 'into_pulp_and_crown_dentin',
  into_pulp_and_root_dentin: 'into_pulp_and_root_dentin',
  into_pulp_and_root_dentin_and_crown_dentin:
    'into_pulp_and_root_dentin_and_crown_dentin',
  unknown: 'unknown',
} as const;
export type TCariesDepth = ValueOf<typeof CariesDepth>;

export type CariesDepthDisplayMap = {
  [K in keyof typeof CariesDepth]: string;
};

export const cariesDepthDisplayMap: CariesDepthDisplayMap = {
  into_crown_and_root_dentin: 'Into dentin',
  into_crown_dentin: 'Into dentin',
  into_enamel: 'Into enamel',
  into_furcation_and_root_dentin: 'Into furcation',
  into_furcation_and_root_dentin_and_pulp: 'Into furcation',
  into_pulp: 'Into pulp',
  into_pulp_and_crown_dentin: 'Into pulp',
  into_pulp_and_root_dentin: 'Into pulp',
  into_pulp_and_root_dentin_and_crown_dentin: 'Into pulp',
  into_root_dentin: 'Root dentin involvement',
  unknown: 'Unknown',
};

function isValidKey(key: string): key is keyof CariesDepthDisplayMap {
  return key in cariesDepthDisplayMap;
}

export const displayFindingDepth = (
  depth?: string | keyof CariesDepthDisplayMap | null,
) => {
  if (!depth || depth === 'N/A') {
    return 'Not calculated';
  }
  if (!isValidKey(depth)) {
    return depth;
  }
  return cariesDepthDisplayMap[depth];
};

export const CariesRootInvolvement = {
  no_root_involvement: 'no_root_involvement',
  root_and_crown_involvement: 'root_and_crown_involvement',
  root_involvement: 'root_involvement',
} as const;
export type TCariesRootInvolvement = ValueOf<typeof CariesRootInvolvement>;

type TSegmentation = {
  id: string;
  /**
   * segmentation - main outline
   */
  outline?: MultiPolygon;
  confidenceScore?: number;
  /**
   * analyses."UserValidations".status
   */
  userValidation?: TUserValidationStatus;
  bin?: number | null;
};

export const CariesSeverity = {
  NA: 'NA',
  INCIPIENT: 'INCIPIENT',
  INITIAL: 'INITIAL',
  MODERATE: 'MODERATE',
  ADVANCED: 'ADVANCED',
  SEVERE: 'SEVERE',
} as const;
export type TCariesSeverity = ValueOf<typeof CariesSeverity>;

export const UserValidationStatus = {
  agree: 'agree',
  disagree: 'disagree',
  monitor: 'monitor',
} as const;
export type TUserValidationStatus = ValueOf<typeof UserValidationStatus>;
